import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listInbound, sendReply, ApiBaseUrl } from '../api';
import Avatar from '../components/Avatar';

// Helper: format a date into a WhatsApp-style label
function getDateLabel(date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function InboxPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toPhone, setToPhone] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [query, setQuery] = useState('');

  // Helper to normalize phone numbers (digits only)
  const normalizePhone = (p) => (p || '').replace(/\D/g, '');

  const load = async () => {
    setLoading(true);
    try {
      const items = await listInbound(200);
      setMessages(items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  const onSend = async (e) => {
    e.preventDefault();
    const rawPhone = toPhone || selectedPhone;
    if (!rawPhone || !messageText) return;
    const targetPhone = normalizePhone(rawPhone);
    setSending(true);
    setError('');
    // optimistic append
    const optimistic = {
      _id: `local-${Date.now()}`,
      toPhone: targetPhone,
      fromPhone: undefined,
      type: 'text',
      text: messageText,
      outbound: true,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      await sendReply({
        toPhone: targetPhone,
        messageText,
        senderPhoneId: process.env.REACT_APP_WHATSAPP_PHONE_ID
      });
      setMessageText('');
      // Set selectedPhone to normalized version to match incoming data
      setSelectedPhone(targetPhone);
      setToPhone(targetPhone);
      setTimeout(load, 500);
    } catch (err) {
      console.error('Reply failed:', err);
      const msg = err.response?.data?.error?.error?.message || err.response?.data?.error?.message || 'Failed to send reply';
      setError(msg);
      // Remove the optimistic message on failure
      setMessages(prev => prev.filter(m => m._id !== optimistic._id));
    } finally {
      setSending(false);
    }
  };

  const threads = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const key = normalizePhone(m.fromPhone || m.toPhone);
      if (!key) continue;
      const t = map.get(key) || { phone: key, name: m.contactName, lastAt: 0, lastText: '' };
      const ts = new Date(m.createdAt || m.timestamp || Date.now()).getTime();
      if (ts > t.lastAt) {
        t.lastAt = ts;
        if (m.type === 'text') t.lastText = m.text;
        else if (m.type === 'image') t.lastText = '📷 Photo';
        else if (m.type === 'video') t.lastText = '🎥 Video';
        else if (m.type === 'audio') t.lastText = '🎵 Audio';
        else if (m.type === 'sticker') t.lastText = '🎨 Sticker';
        else if (m.type === 'document') t.lastText = '📄 Document';
        else if (m.type === 'location') t.lastText = '📍 Location';
        else if (m.type === 'contacts') t.lastText = '👤 Contact';
        else if (m.type === 'reaction') t.lastText = `Reacted ${m.reaction?.emoji || ''}`;
        else if (m.type === 'interactive') t.lastText = m.text || 'Interactive Message';
        else t.lastText = m.text || m.type;

        if (m.contactName) t.name = m.contactName;
      }
      map.set(key, t);
    }
    const arr = Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      return arr.filter(t => t.phone.toLowerCase().includes(q) || (t.lastText || '').toLowerCase().includes(q));
    }
    return arr;
  }, [messages, query]);

  const filteredMessages = useMemo(() => {
    if (!selectedPhone) return [];
    const normalizedSelected = normalizePhone(selectedPhone);
    return messages
      .filter(m => normalizePhone(m.fromPhone) === normalizedSelected || normalizePhone(m.toPhone) === normalizedSelected)
      .sort((a, b) => new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp));
  }, [messages, selectedPhone]);

  return (
    <div className="yc-chat yc-chat-split">
      <aside className="yc-chat-threads">
        <div className="yc-threads-search">
          <input className="yc-input" placeholder="Search phone..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="yc-threads-list">
          {threads.map(t => (
            <div key={t.phone} className={`yc-thread-item ${normalizePhone(selectedPhone) === normalizePhone(t.phone) ? 'active' : ''}`} onClick={() => { setSelectedPhone(normalizePhone(t.phone)); setToPhone(normalizePhone(t.phone)); }}>
              <Avatar phone={t.phone} name={t.name} />
              <div className="yc-thread-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div className="yc-thread-title">{t.name || t.phone}</div>
                  <div className="yc-thread-time">{new Date(t.lastAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div className="yc-thread-sub">{t.lastText}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>
      <section className="yc-chat-right">
        <div ref={listRef} className="yc-chat-messages">
          {!selectedPhone && (
            <div className="yc-chat-empty">Select a contact to view messages</div>
          )}
          {selectedPhone && (
            <div className="yc-bubbles">
              {filteredMessages.map((m, idx) => {
                const isOutgoing = m.outbound || (!m.fromPhone && m.toPhone === selectedPhone);
                const isIncoming = !isOutgoing;
                const msgDate = new Date(m.createdAt || m.timestamp);
                const prevDate = idx > 0 ? new Date(filteredMessages[idx - 1].createdAt || filteredMessages[idx - 1].timestamp) : null;
                const showDateSep = !prevDate ||
                  msgDate.getFullYear() !== prevDate.getFullYear() ||
                  msgDate.getMonth() !== prevDate.getMonth() ||
                  msgDate.getDate() !== prevDate.getDate();

                return (
                  <React.Fragment key={m._id}>
                    {showDateSep && (
                      <div className="yc-date-separator">
                        <span>{getDateLabel(msgDate)}</span>
                      </div>
                    )}
                    <div className={`yc-bubble-container ${isIncoming ? 'in' : 'out'}`}>
                      {isIncoming && <Avatar phone={selectedPhone} name={m.contactName} size="sm" />}
                      <div className={`yc-bubble ${isIncoming ? 'in' : 'out'}`}>
                        <div className="yc-bubble-meta">
                          <span className="yc-bubble-time">{new Date(m.createdAt || m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="yc-bubble-content">
                          {m.type === 'text' && m.text && (
                            <span>{m.text}</span>
                          )}
                          {m.type === 'image' && m.image && (
                            <div>
                              <div>📷 Image</div>
                              {m.image.caption && <div style={{ fontSize: '0.9em', color: '#666' }}>&quot;{m.image.caption}&quot;</div>}
                              <div style={{ fontSize: '0.8em', color: '#999' }}>{m.image.mimeType}</div>
                              <div style={{ marginTop: 6 }}>
                                <img
                                  alt={m.image.caption || 'image'}
                                  src={`${ApiBaseUrl}/inbound/media/${m.whatsappMessageId}/image`}
                                  style={{ maxWidth: 260, maxHeight: 260, borderRadius: 4, border: '1px solid #eee' }}
                                />
                              </div>
                            </div>
                          )}
                          {m.type === 'video' && m.video && (
                            <div>
                              <div>🎥 Video</div>
                              {m.video.caption && <div style={{ fontSize: '0.9em', color: '#666' }}>&quot;{m.video.caption}&quot;</div>}
                              <div style={{ fontSize: '0.8em', color: '#999' }}>{m.video.mimeType}</div>
                              <div style={{ marginTop: 6 }}>
                                <video
                                  controls
                                  style={{ width: 320, maxWidth: '100%', borderRadius: 4, border: '1px solid #eee' }}
                                  src={`${ApiBaseUrl}/inbound/media/${m.whatsappMessageId}/video`}
                                />
                              </div>
                            </div>
                          )}
                          {m.type === 'document' && m.document && (
                            <div>
                              <div>📄 Document</div>
                              {m.document.filename && <div style={{ fontSize: '0.9em', color: '#666' }}>{m.document.filename}</div>}
                              {m.document.caption && <div style={{ fontSize: '0.9em', color: '#666' }}>"{m.document.caption}"</div>}
                              <div style={{ fontSize: '0.8em', color: '#999' }}>{m.document.mimeType}</div>
                              <a
                                href={`${ApiBaseUrl}/inbound/media/${m.whatsappMessageId}/document`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '0.8em', color: '#007bff', textDecoration: 'none' }}
                              >
                                Download Document
                              </a>
                            </div>
                          )}
                          {m.type === 'audio' && m.audio && (
                            <div>
                              <div>🎵 Audio</div>
                              <div style={{ fontSize: '0.8em', color: '#999' }}>{m.audio.mimeType}</div>
                              <div style={{ marginTop: 6 }}>
                                <audio
                                  controls
                                  style={{ width: 320, maxWidth: '100%' }}
                                  src={`${ApiBaseUrl}/inbound/media/${m.whatsappMessageId}/audio`}
                                />
                              </div>
                            </div>
                          )}
                          {m.type === 'contacts' && m.contacts && m.contacts.length > 0 && (
                            <div>
                              <div>👤 Contact{m.contacts.length > 1 ? 's' : ''}</div>
                              {m.contacts.map((contact, idx) => (
                                <div key={idx} style={{ fontSize: '0.9em', color: '#666', marginTop: 4 }}>
                                  <div>{contact.name?.formatted_name || contact.name?.first_name || 'Unknown'}</div>
                                  {contact.phones && contact.phones.length > 0 && (
                                    <div style={{ fontSize: '0.8em', color: '#999' }}>
                                      {contact.phones.map((phone, pIdx) => (
                                        <span key={pIdx}>{phone.phone}{pIdx < contact.phones.length - 1 ? ', ' : ''}</span>
                                      ))}
                                    </div>
                                  )}
                                  {contact.vcard && (
                                    <div style={{ marginTop: 4 }}>
                                      <a
                                        href={`${ApiBaseUrl}/inbound/vcard/${m.whatsappMessageId}/${idx}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: '0.8em', color: '#007bff', textDecoration: 'none' }}
                                      >
                                        Download vCard
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {m.type === 'location' && m.location && (
                            <div>
                              <div>📍 Location</div>
                              {m.location.name && <div style={{ fontSize: '0.9em', color: '#666' }}>{m.location.name}</div>}
                              {m.location.address && <div style={{ fontSize: '0.9em', color: '#666' }}>{m.location.address}</div>}
                              <div style={{ fontSize: '0.8em', color: '#999' }}>
                                {m.location.latitude}, {m.location.longitude}
                              </div>
                              {(typeof m.location.latitude === 'number' || typeof m.location.latitude === 'string') && (typeof m.location.longitude === 'number' || typeof m.location.longitude === 'string') && (
                                <div style={{ marginTop: 4 }}>
                                  <a
                                    href={`https://www.google.com/maps?q=${m.location.latitude},${m.location.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '0.8em', color: '#007bff', textDecoration: 'none' }}
                                  >
                                    Open in Google Maps
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                          {m.type === 'sticker' && m.sticker && (
                            <div>
                              <div>🎨 Sticker</div>
                              <div style={{ marginTop: 6 }}>
                                <img
                                  alt="sticker"
                                  src={`${ApiBaseUrl}/inbound/media/${m.whatsappMessageId}/sticker`}
                                  style={{ maxWidth: 160, maxHeight: 160 }}
                                />
                              </div>
                            </div>
                          )}
                          {m.type === 'reaction' && m.reaction && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.05)', padding: '4px 8px', borderRadius: 12, width: 'fit-content' }}>
                              <span style={{ fontSize: '1.4em' }}>{m.reaction.emoji}</span>
                              <span style={{ fontSize: '0.8em', color: '#666' }}>Reacted to a message</span>
                            </div>
                          )}
                          {m.type === 'interactive' && m.interactive && (
                            <div style={{ borderLeft: '3px solid #007bff', paddingLeft: 8 }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.9em' }}>{m.interactive.type === 'button_reply' ? 'Button Reply' : 'List Reply'}</div>
                              <div style={{ fontSize: '1em' }}>{m.text}</div>
                              {m.interactive.list_reply?.description && (
                                <div style={{ fontSize: '0.85em', color: '#666' }}>{m.interactive.list_reply.description}</div>
                              )}
                            </div>
                          )}
                          {!['text', 'image', 'video', 'document', 'audio', 'contacts', 'location', 'sticker', 'reaction', 'interactive'].includes(m.type) && (
                            <span style={{ color: '#999' }}>Unsupported message type ({m.type})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <form onSubmit={onSend} className="yc-composer">
          {error && <div className="yc-composer-error" style={{ color: '#dc3545', fontSize: '0.85em', padding: '4px 12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            <input
              placeholder="Type a message"
              value={messageText}
              onChange={(e) => { setMessageText(e.target.value); if (error) setError(''); }}
              className="yc-input yc-input-message"
            />
            <button disabled={sending} type="submit" className="yc-send-btn">{sending ? 'Sending...' : 'Send'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

